import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { connect } from 'react-redux'
import { cloneDeep, findKey, isEqual, values } from 'lodash'
import memoize from 'memoize-one'

import ConflictModal from '../../components/conflictModal/ConflictModal'
import {
  fetchShipsUsingAMmsilistFromBackend,
  fetchCargoOperationsDetailsFromBackendRest,
  fetchAllShipDetailsInTheRadius
} from '../../js/rest'
import TerminalPlanner from '../../components/terminalPlanner/TerminalPlanner'
import {
  getAtaOrCargoStart,
  getAtdOrCargoEnd,
  getConflicts,
  getOverlappingTerminalVisits
} from '../../utils/terminalVisitConflictsUtils'
import './CoordinatorView.scss'
import VisitsWebSocket from '../../components/visitsWebSocket/VisitsWebSocket'
import CoordinatorVisitsList from '../../components/coordinatorVisitsList/CoordinatorVisitsList'
import { setSelectedVisit } from '../../components/app/actions'
import { handleFetchError } from '../../js/sentry'
import { createVisitOrderPairs } from '../../utils/terminalVisitUtils'
import Modal from '../../components/app/customModal/Modal'
import CargoOperationsDetails from '../../components/terminalVisitForm/cargoOperationsDetails/CargoOperationsDetails'
import { CARGO_DETAILS_CONFIG, VISIT_STATUSES } from '../../js/constants'
import { reportPageView } from '../../utils/analyticsUtils'
import ShipsMap from '../../components/shipsmap/ShipsMap'

class CoordinatorView extends PureComponent {
  static propTypes = {
    customFetch: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)

    const cargoDetailsType = findKey(CARGO_DETAILS_CONFIG, config =>
      config.includes(props.app.present.config.terminalId)
    )

    this.state = {
      cargoDetailsForAllVisits: [],
      openCargoDetailsForTerminalVisit: null,
      cargoDetailsType
    }
  }

  componentDidMount() {
    this.fetchCargoOperationsDetails()
    reportPageView('/home/coordinator')
  }

  componentDidUpdate(prevProps) {
    const terminalVisits = this.props.app.present.terminalVisits
    const prevTerminalVisits = prevProps.app.present.terminalVisits
    if (!isEqual(terminalVisits, prevTerminalVisits)) {
      this.fetchCargoOperationsDetails()
    }
  }

  // memoize the last returned result of conflicts to preventing re-render of ShipVisitList every time
  getConflictsMemoized = memoize(getConflicts)

  render() {
    const { conflicts } = this.getConflictsMemoized(
      this.props.app.present.terminalVisits,
      this.props.app.present.config.quays,
      this.props.app.present.config.etaTooLateThreshold
    )

    const terminalVisitsWithOverlap = getOverlappingTerminalVisits(
      this.props.app.present.terminalVisits,
      this.props.app.present.config.quays
    )

    const filteredVisitsTwoDays = this.filterVisitsTwoDays(this.props.app.present.terminalVisits)

    // For other
    const { ship, order, terminalVisitId } = this.state.openCargoDetailsForTerminalVisit || {
      order: null,
      ship: null,
      terminalVisitId: null
    }
    const identifiers = {
      terminalId: this.props.app.present.config.terminalId,
      terminalVisitId,
      orderId: order ? order.uuid : null
    }
    const shipName = ship ? ship.name : null

    return (
      <div className="coordinator-viewer main">
        <Modal.Wrapper
          isOpen={!!this.state.openCargoDetailsForTerminalVisit}
          title={`Cargo Operations Details ${shipName ? '- ' + shipName : ''}`}
          onCloseModal={this.handleCargoDetailsClose}
        >
          <CargoOperationsDetails
            ship={ship}
            identifiers={identifiers}
            order={order}
            authToken={this.props.login.auth.token}
            customFetch={this.props.customFetch}
            readOnly={false}
            closeModal={this.handleCargoDetailsClose}
            terminalName={this.props.app.present.config.name}
          />
        </Modal.Wrapper>

        <VisitsWebSocket customFetch={this.props.customFetch} currentTerminalId={this.props.login.auth.terminalId}>
          <ConflictModal
            conflictModalDimensions={this.props.app.present.conflictModalDimensions}
            conflictModalConflicts={this.props.app.present.conflictModalConflicts}
          />

          <div className="terminal-viewer-content">
            <div className="main-wrapper">
              {!this.props.ui.terminalPlannerExpanded ? (
                <div className="box-element">
                  <CoordinatorVisitsList
                    terminalVisits={filteredVisitsTwoDays}
                    statuses={values(VISIT_STATUSES)}
                    scrollBarWidth={this.props.ui.scrollBarWidth}
                    cargoOperationDetails={this.state.cargoDetailsForAllVisits}
                    openShipDetailsModal={this.handleOpenShipDetailsModal}
                  />
                </div>
              ) : null}

              <div className="box-element big">
                <TerminalPlanner
                  conflicts={conflicts}
                  terminalVisitsWithOverlap={terminalVisitsWithOverlap}
                  customFetch={this.props.customFetch}
                  isAllowedToEdit={false}
                  defaultShortTerm
                  onSelectTerminalVisit={this.handleSelectTerminalVisit}
                />
              </div>
            </div>

            {!this.props.ui.terminalPlannerExpanded ? (
              <div className="main-container box-element right-sidebar">
                <ShipsMap
                  config={this.props.app.present.config}
                  getShipsDetailsByMmsiList={this.getShipsDetailsByMmsiList}
                  terminalVisits={filteredVisitsTwoDays.filter(
                    terminalVisit => terminalVisit.ship && terminalVisit.ship.mmsi
                  )}
                  zoomToFitShips={false}
                  showShipPopupWithEta
                  getShipDetailsInTheRadius={this.getShipDetailsInTheRadius}
                />
              </div>
            ) : null}
          </div>
        </VisitsWebSocket>
      </div>
    )
  }

  /**
   * When the user selects a visit -> set it in store
   * @param {TerminalVisit?} selectedVisit
   * @param {string?} selectedOrderId
   */
  handleSelectTerminalVisit = (selectedVisit, selectedOrderId) => {
    this.props.dispatch(setSelectedVisit(selectedVisit, selectedOrderId))
  }

  /**
   * @param {TerminalVisitShip} ship
   * @param {Order} order
   * @param {TerminalVisit['_id']} terminalVisitId
   */
  handleOpenShipDetailsModal = (ship, order, terminalVisitId) => {
    this.setState({ openCargoDetailsForTerminalVisit: { ship, order, terminalVisitId } })
  }

  /**
   * When the cargo details modal close, refetch the cargo details in case of changes
   */
  handleCargoDetailsClose = () => {
    this.fetchCargoOperationsDetails()
    this.setState({ openCargoDetailsForTerminalVisit: null })
  }

  fetchCargoOperationsDetails = async () => {
    const config = this.props.app.present.config
    const visitsForTwoDays = this.filterVisitsTwoDays(this.props.app.present.terminalVisits)
    const allOrders = createVisitOrderPairs(visitsForTwoDays)

    const promisesCargoDetails = allOrders.map(async terminalVisit => {
      const identifiers = {
        terminalId: config.terminalId,
        terminalVisitId: terminalVisit.visit._id,
        orderId: terminalVisit.order.uuid
      }

      try {
        return await fetchCargoOperationsDetailsFromBackendRest(
          identifiers,
          this.state.cargoDetailsType,
          this.props.login.auth.token,
          this.props.customFetch
        )
      } catch (err) {
        if (err.status !== 404) {
          handleFetchError(err)
        }
        return null
      }
    })

    const cargoDetailsForAllVisits = await Promise.all(promisesCargoDetails)

    this.setState({ cargoDetailsForAllVisits: cargoDetailsForAllVisits.filter(details => details) })
  }

  /**
   * @param {TerminalVisit[]} terminalVisits
   * @returns {TerminalVisit[]}
   */
  filterVisitsTwoDays = terminalVisits => {
    return cloneDeep(terminalVisits)
      .map(visit => {
        visit.orders = visit.orders.filter(order => {
          const endTime = moment(getAtaOrCargoStart(visit, order))
          const startTime = moment(getAtdOrCargoEnd(visit, order))

          const endsLaterThanTwoDays = endTime.isAfter(moment(new Date()).add(2, 'days'))
          const startEarlierThanNow = startTime.isBefore(moment(new Date()))

          const startIsBetweenNowAndTwoDays = startTime.isBetween(moment(new Date()), moment(new Date()).add(2, 'days'))
          const endIsBetweenNowAndTwoDays = endTime.isBetween(moment(new Date()), moment(new Date()).add(2, 'days'))

          return (
            startIsBetweenNowAndTwoDays || endIsBetweenNowAndTwoDays || (startEarlierThanNow && endsLaterThanTwoDays)
          )
        })
        return visit
      })
      .filter(visit => visit.orders.length)
  }

  getShipsDetailsByMmsiList = mmsilist =>
    fetchShipsUsingAMmsilistFromBackend(mmsilist, this.props.login.auth.token, this.props.customFetch)

  getShipDetailsInTheRadius = async (center, radius) => {
    return fetchAllShipDetailsInTheRadius(center, radius, this.props.login.auth.token, this.props.customFetch)
  }
}

export default connect(state => state)(CoordinatorView)
